/*
* Copyright 2022, Corning Optical Communications
* All Rights Reserved.
*/
import React, { useEffect, useState } from "react";
import { useStoreDispatch } from "../../../../store/hooks";
import CustomDataGrid from "../../../Shared/Table/CustomDataGrid";
import { useAddEditEmailGroup } from "./hooks";
import AddEditEmailGroup from "./AddEditEmailGroup";
import { setBasicDialogOpenFor, setConfirmDialog } from "../../../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { basicDialogSelector, confirmDialogSelector } from "../../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import { setIsCreateEmailGroup, setSelectedEmailGroup } from "../../../../store/api/admin/reducers";
import { resetEmailGroup } from "../../../helpers";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../../globals/constants";
import { prepareEmailGroupCellDataGrid } from "../../../../pages/Configuration/helpers";
import { ISnmpDataGridRow } from "../../../../store/api/deviceConfig/types";
import { sanitizeHtmlInput } from "../../../../utils/helpers";
import { Button } from "../../../ui/button";
import { Icon } from "../../../Shared/Icon";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../ui/dropdown-menu";
import { GridColDef } from "@mui/x-data-grid";

const EmailGroups: React.FC = () => {
  const dispatch = useStoreDispatch();
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const [modelOpenMode, setModelOpenMode] = React.useState("Add");
  const [groupName, setGroupName] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Sync local drawer state with Redux (e.g., when drawer closes itself)
  useEffect(() => {
    if (basicDialogOpenFor !== DIALOGOPENFOR.EMAILGROUPS) {
      setIsDrawerOpen(false);
    }
  }, [basicDialogOpenFor]);

  const { data = [], isLoading, emailGroupsColumns, getSelectedSnmpGroup, handleDeleteEmailGroup } =
    useAddEditEmailGroup();

  const rowsData = prepareEmailGroupCellDataGrid(data) || [];
  const noOfRows = rowsData?.length || 0;

  const handleOnAddEmailGroup = () => {
    dispatch(setSelectedEmailGroup(resetEmailGroup));
    dispatch(setIsCreateEmailGroup(true));
    setModelOpenMode("Add");
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.EMAILGROUPS));
  };

  const handleEditRow = (row: any) => {
    dispatch(setSelectedEmailGroup(row));
    dispatch(setIsCreateEmailGroup(false));
    setModelOpenMode("Edit");
    setGroupName(row?.name);
    getSelectedSnmpGroup(row?.name);
    setIsDrawerOpen(true);
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.EMAILGROUPS));
  };

  const handleDeleteRow = (row: any) => {
    setGroupName(row?.name);
    dispatch(setConfirmDialog(DIALOGOPENFOR.EMAILGROUPS));
  };

  const handleDeleteEmail = (actionName: string) => {
    if (!actionName) return;
    if (groupName !== null) {
      handleDeleteEmailGroup(sanitizeHtmlInput(groupName), actionName);
    }
  };

  const actionsColumn: GridColDef = {
    field: "actions",
    headerName: "",
    width: 60,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 border-0 bg-transparent rounded-full text-foreground"
            aria-label="Row actions"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              const row = params.row;
              setTimeout(() => handleEditRow(row), 200);
            }}
          >
            <Pencil className="h-4 w-4" fill="currentColor" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => {
              const row = params.row;
              setTimeout(() => handleDeleteRow(row), 200);
            }}
          >
            <Trash2 className="h-4 w-4" fill="currentColor" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  };

  return (
    <div className="flex flex-col justify-center items-start w-full">
      {openConfirmDialog === DIALOGOPENFOR.EMAILGROUPS && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteEmail}>
          Are you sure you want to delete this email group <strong>{sanitizeHtmlInput(groupName) || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      {(basicDialogOpenFor === DIALOGOPENFOR.EMAILGROUPS || isDrawerOpen) && <AddEditEmailGroup modelOpenMode={modelOpenMode} />}
      <div className="flex items-center justify-end w-full px-3 mb-1">
        <Button variant="outline" onClick={handleOnAddEmailGroup} style={{ backgroundColor: '#004f69', color: '#fff', borderColor: '#004f69' }}>
          <Icon name="add" size={18} />
          Add Email Group
        </Button>
      </div>

      <div className="flex justify-between items-end ml-3 mb-1 mt-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{`${noOfRows || 0} results`}</span>
        </div>
      </div>

      <div className="w-full pl-3 pr-3">
        <CustomDataGrid
          tableId="emailGroup"
          rows={rowsData}
          columns={[...emailGroupsColumns, actionsColumn]}
          checkboxSelection={false}
          rowCount={noOfRows}
          loading={isLoading}
          disableColumnMenu={true}
          getRowId={(row: ISnmpDataGridRow) => row.id}
          paginationMode="client"
          fillContainer
        />
      </div>
    </div>
  );
};

export default EmailGroups;
